import PhysicsEngine from './lib/physics-engine.js';
import MomentumEngine from './lib/momentum-engine.js';
import SettleEngine from './lib/settle-engine.js';
import { getTrackPosForIndex, getIndexForTrackPos } from './utils/metrics.js';

// Velocity thresholds for gear shifts
const SNAP_THRESHOLD = 14; // Velocity below which snap kicks in (for snap: 'slide')
const STOP_THRESHOLD = 0.01; // Velocity below which we consider stopped (for snap: 'none')

// Manages track animations using physics-based motion
export default class TrackAnimator {
	#currentPos = 0;
	#dragStartPos = 0;
	#minVelocity = 0;
	#targetPos = 0;
	#movementType = '';
	#direction = 0;
	#isDriving = false;
	#driveMovementType = 'scroll';

	// Transmission pattern: active engine pointer
	#activeEngine = null; // Current "gear": null, momentumEngine, settleEngine, or physicsEngine
	#momentumEngine = null;
	#settleEngine = null;

	/**
	 * @param {Object} ctx - The context object containing carousel references and services
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		const emitter = ctx.emitter;

		// get initial animation options from store
		const options = _.ctx.store.getOptions();

		// load new physics engine with attraction and friction options
		_.engine = new PhysicsEngine({
			attraction: options.animation.attraction,
			friction: options.animation.friction,
			velocityBoost: options.animation.velocityBoost,
		});

		// create momentum engine for free scroll modes
		_.#momentumEngine = new MomentumEngine({
			friction: options.animation.freeScrollFriction,
		});

		// create settle engine for smooth snap-to-slide settling
		_.#settleEngine = new SettleEngine();

		_.#dragStartPos = 1;

		// Define event handlers to enable proper cleanup
		_.handlers = {
			optionsChanged: ({ currentOptions }) => {
				_.engine.setAttraction(currentOptions.animation?.attraction || 0.026);
				_.engine.setFriction(currentOptions.animation?.friction || 0.24);
				_.engine.setVelocityBoost(currentOptions.animation?.velocityBoost || 1.4);
				_.#momentumEngine.setFriction(currentOptions.animation?.freeScrollFriction);
			},
			settlePositionChanged: ({ position, positionDelta, progress, velocity }) => {
				if (progress === 1) {
					_.setPos(position, progress, velocity, 'settle', _.#direction);
				} else {
					_.setPos(_.#currentPos + positionDelta, progress, velocity, 'settle', _.#direction);
				}
			},
			settleFinished: () => {
				_.#activeEngine = null;
				_.#emitMovementCompleted('settle');
			},
			dragStart: ({ event, drag }) => {
				_.stop();
				_.#dragStartPos = _.#currentPos;

				emitter.emit(_.ctx.events.user.interacted, { via: 'drag', event });
			},
			dragMove: ({ event, drag }) => {
				_.setPos(_.#dragStartPos + drag.delta, 1, null, 'drag', 0); // direction = 0 for drag
				emitter.emit(_.ctx.events.user.interacted, { via: 'drag', event });
			},
			dragEnd: ({ event, drag }) => {
				emitter.emit(_.ctx.events.user.interacted, { via: 'drag', event });

				const options = _.ctx.store.getOptions();

				if (Math.abs(drag.delta) < options.dragThreshold) {
					_.ctx.commands.settleTrack();
					return;
				}

				// Check snap mode to determine behavior
				if (options.snap === 'page') {
					// Current behavior - go to next/previous page
					if (drag.delta < 0) {
						_.ctx.commands.next(drag.velocity);
					} else {
						_.ctx.commands.previous(drag.velocity);
					}
				} else {
					// Free scroll modes ('slide' or 'none')
					// Start momentum engine with current position and velocity
					_.startMomentum(_.#currentPos, drag.velocity * 1.3);
				}
			},
			enginePositionChanged: ({ position, positionDelta, progress, velocity }) => {
				if (progress === 1) {
					// we have reached the end
					_.setPos(_.#targetPos, progress, velocity, _.#movementType, _.#direction);
				} else {
					// add delta to current position
					_.setPos(
						_.#currentPos + positionDelta,
						progress,
						velocity,
						_.#movementType,
						_.#direction
					);
				}
			},
			engineMovementFinished: () => {
				_.#activeEngine = null;
				_.#emitMovementCompleted(_.#movementType);
			},
			requestTrackFrame: ({ time }) => {
				// Transmission pattern: tick whichever engine is active
				if (_.#activeEngine === _.#momentumEngine) {
					// Momentum gear is engaged
					_.#momentumEngine.tick(time);

					// Update track position
					const position = _.#momentumEngine.position;
					_.setPos(position, 0, _.#momentumEngine.velocity, 'momentum', 0);

					// Check for gear shift conditions
					_.#checkMomentumTransition();
				} else if (_.#activeEngine === _.#settleEngine) {
					// Settle gear is engaged - adaptive friction settling
					_.#settleEngine.tick(time);
				} else if (_.#isDriving) {
					// Drive gear is engaged - no-op; position is driven externally
				} else if (_.#activeEngine === _.engine) {
					// Physics gear is engaged - use existing physics engine tick
					_.engine.tick(time);
				} else {
					// Neutral - tick physics engine for legacy behavior
					_.engine.tick(time);
				}
			},
		};

		_.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		const _ = this;
		const { emitter, events } = _.ctx;

		// Bind context events using handlers object for proper cleanup
		emitter.on(events.store.optionsChanged, _.handlers.optionsChanged);
		emitter.on(events.drag.start, _.handlers.dragStart);
		emitter.on(events.drag.move, _.handlers.dragMove);
		emitter.on(events.drag.end, _.handlers.dragEnd);
		emitter.on(events.track.requestFrame, _.handlers.requestTrackFrame);

		// Bind physics engine events
		_.engine.on('engine:position-changed', _.handlers.enginePositionChanged);
		_.engine.on('engine:finished', _.handlers.engineMovementFinished);

		// Bind settle engine events
		_.#settleEngine.on('engine:position-changed', _.handlers.settlePositionChanged);
		_.#settleEngine.on('engine:finished', _.handlers.settleFinished);
	}

	// @returns {number} - The current X position.
	get currentPos() {
		return this.#currentPos;
	}

	/**
	 * Sync renderIndex and pageIndex from a track position
	 * Used by driveTrackPosition and momentum to keep indices current
	 * @param {number} position - Current track position (post-shift)
	 * @param {number} [velocity=0] - Current velocity for direction bias
	 */
	syncIndicesFromPosition(position, velocity = 0) {
		const _ = this;
		const state = _.ctx.store.getState();
		const widths = _.ctx.store.getWidths();
		const options = _.ctx.store.getOptions();
		const currentIndex = getIndexForTrackPos(position, widths, options, velocity, state.slideCount);
		const slidesPerMove = options.slidesPerMove || 1;
		const currentPageIndex = Math.max(
			0,
			Math.min(Math.floor(currentIndex / slidesPerMove), state.pageCount - 1)
		);
		if (currentIndex !== state.renderIndex || currentPageIndex !== state.pageIndex) {
			_.ctx.store.setState({
				renderIndex: currentIndex,
				pageIndex: currentPageIndex,
			});
		}
	}

	getIsAnimating() {
		return (
			this.engine.isAnimating() || this.#momentumEngine.isRunning || this.#settleEngine.isRunning
		);
	}

	stop() {
		const _ = this;
		_.#isDriving = false;
		_.engine.stop();
		_.#momentumEngine.stop();
		_.#settleEngine.stop();
		_.#activeEngine = null;
	}

	goToPosition(targetPos, velocity, movementType, direction = 0) {
		const _ = this;

		// don't interrupt if we're already animating to this exact position
		if (movementType !== 'jump' && _.engine.isAnimating() && _.#targetPos === targetPos) {
			return;
		}

		// stop any current animations
		_.stop();

		// save movement type and direction for later
		_.#movementType = movementType;
		_.#direction = direction;

		// check to see if it's a jump and then exit
		if (movementType === 'jump') {
			_.#emitMovementStarted(0, 'jump');
			_.setPos(targetPos, 1, 0, 'jump', direction);
			_.#emitMovementCompleted('jump');
			return;
		}

		// calculate min velocity
		if (!velocity) {
			if (targetPos < _.#currentPos) {
				velocity = _.#minVelocity * -1;
			} else {
				velocity = _.#minVelocity;
			}
		}

		// save target position
		_.#targetPos = targetPos;

		// tell engine to go to target with velocity
		_.engine.animateTo(_.#currentPos, targetPos, velocity);

		// emit movement started event
		_.#emitMovementStarted(velocity, movementType);
	}

	// movementType = 'animate', 'jump', 'settle', 'drag', 'scroll'
	setPos(newPosition, progress, velocity = 0, movementType, direction = null) {
		const _ = this;
		let trackDelta = newPosition - _.#currentPos;

		// final delta is 0 for completed non-continuous movements
		if (movementType !== 'drag' && movementType !== 'scroll' && progress === 1) {
			trackDelta = 0;
		}

		// save new position
		_.#currentPos = newPosition;

		// check for automatic loop shifting (only if loop is enabled)
		if (_.ctx.store.getState().canLoop) {
			const trackWidth = _.ctx.store.getWidths().track;

			if (_.#currentPos > 0) {
				// track has moved too far right, shift forwards (left)
				_.shiftTrack('forwards');
			} else if (_.#currentPos <= -trackWidth) {
				// track has moved too far left, shift backwards (right)
				_.shiftTrack('backwards');
			}
		}

		// update track position in animation data
		_.ctx.store.setAnimation({
			movementType: movementType,
			trackPosition: _.#currentPos,
			trackDelta,
			velocity,
			progress,
			isAnimating: _.getIsAnimating(),
			direction: direction !== null ? direction : _.#direction,
		});
	}

	// direction = 'forwards' (shift left) or 'backwards' (shift right), or numeric value
	shiftTrack(direction = 1) {
		const _ = this;
		const trackWidth = _.ctx.store.getWidths().track;

		// convert string directions to numeric values
		let value;
		if (direction === 'forwards') {
			value = -1; // shift left (towards negative)
		} else if (direction === 'backwards') {
			value = 1; // shift right (towards positive)
		} else {
			value = direction; // assume numeric value
		}

		const shiftAmount = trackWidth * value;

		// shift track by full length
		_.#currentPos += shiftAmount;
		_.#dragStartPos += shiftAmount;
		_.#targetPos += shiftAmount;

		// Also shift active engine position if running
		if (_.#activeEngine === _.#momentumEngine && _.#momentumEngine.isRunning) {
			_.#momentumEngine.shiftPosition(shiftAmount);
		} else if (_.#activeEngine === _.#settleEngine && _.#settleEngine.isRunning) {
			_.#settleEngine.shiftPosition(shiftAmount);
		}

		// emit track shift event
		_.ctx.emitter.emit(_.ctx.events.track.shifted, {
			trackPosition: _.#currentPos,
			movementType: 'shift',
		});
	}

	/**
	 * Engage momentum gear - for free scroll drag end
	 * @param {number} position - Current track position
	 * @param {number} velocity - Initial velocity from drag
	 */
	startMomentum(position, velocity) {
		const _ = this;

		// Stop any current animation
		_.stop();

		// Start momentum engine
		_.#momentumEngine.start(position, velocity);
		_.#activeEngine = _.#momentumEngine;
		_.#movementType = 'momentum';

		// Update store to trigger frame loop (isAnimating becomes true)
		_.setPos(position, 0, velocity, 'momentum', 0);

		// Emit movement started
		_.#emitMovementStarted(velocity, 'momentum');
	}

	/**
	 * Drive track position — self-contained, no session management.
	 * Plugins describe intent (deltaPx or positionPercent), core handles positioning.
	 * Auto-engages on first call, auto-disengages when stop() is called or movementType is 'settle'.
	 * @param {Object} params
	 * @param {number} [params.deltaPx] - Relative pixel movement ("move by N px this frame")
	 * @param {number} [params.positionPercent] - Absolute 0-1 track position
	 * @param {number} [params.velocity=0] - Current velocity for direction bias
	 * @param {string} [params.movementType='scroll'] - Movement type ('scroll' or 'settle')
	 */
	drivePosition({ deltaPx, positionPercent, velocity = 0, movementType = 'scroll' }) {
		const _ = this;

		// Settle: disengage drive and snap to nearest page
		if (movementType === 'settle') {
			_.#isDriving = false;
			_.ctx.commands.settleTrack();
			return;
		}

		// Resolve position from intent — bail if no position data provided
		let position;
		if (deltaPx !== undefined) {
			position = _.#currentPos + deltaPx;
		} else if (positionPercent !== undefined) {
			position = _.#resolvePercentToPosition(positionPercent);
		} else {
			return;
		}

		// Auto-engage drive gear on first call (after validation)
		if (!_.#isDriving) {
			_.stop();
			_.#isDriving = true;
			_.#driveMovementType = movementType;
		}

		const direction = velocity !== 0 ? Math.sign(velocity) : Math.sign(position - _.#currentPos);
		_.setPos(position, 1, velocity, _.#driveMovementType, direction);
		_.syncIndicesFromPosition(_.#currentPos, velocity);
	}

	/**
	 * Resolve a 0-1 percent to an absolute track position (loop-aware)
	 * @param {number} percent - 0-1 position (can overflow for loop seam transitions)
	 * @returns {number} Track position in pixels
	 */
	#resolvePercentToPosition(percent) {
		const state = this.ctx.store.getState();
		const widths = this.ctx.store.getWidths();
		const firstSnap = state.firstSnapPoint;
		const lastSnap = state.lastSnapPoint;
		const range = firstSnap - lastSnap;

		if (!Number.isFinite(percent) || range === 0) return firstSnap;

		// Normal range (0-1) or non-loop: simple linear interpolation
		if (!state.canLoop || (percent >= 0 && percent <= 1)) {
			return firstSnap - percent * range;
		}

		// Loop overflow: use loopRange for the seam transition
		const trackWidth = widths.track;
		const loopRange = trackWidth - Math.abs(lastSnap - firstSnap);
		if (loopRange <= 0) return firstSnap - percent * range;

		if (percent > 1) {
			// Forward overflow: past last snap toward first snap
			const overflowDistance = (percent - 1) * loopRange;
			return lastSnap - overflowDistance;
		}

		// Backward overflow: before first snap toward last snap
		const overflowDistance = Math.abs(percent) * loopRange;
		return firstSnap + overflowDistance;
	}

	/** @returns {boolean} Whether drive gear is currently engaged */
	// get isDriving() {
	// 	return this.#isDriving;
	// }

	/**
	 * Check if momentum engine should transition to physics (snap) or stop
	 * Called each frame when momentum gear is engaged
	 */
	#checkMomentumTransition() {
		const _ = this;
		const velocity = _.#momentumEngine.velocity;
		let position = _.#momentumEngine.position;
		const options = _.ctx.store.getOptions();
		const widths = _.ctx.store.getWidths();
		const state = _.ctx.store.getState();
		const slideCount = state.slideCount;

		// Boundary handling for non-looping carousels
		if (!state.canLoop) {
			// Use getTrackPosForIndex for consistent boundary calculation
			// This accounts for padding, centering, and other layout options
			const minPos = getTrackPosForIndex(0, widths, options, state);
			const maxPos = getTrackPosForIndex(slideCount - 1, widths, options, state);

			// Check if we've hit a boundary
			if (position > minPos) {
				// Hit left boundary - stop momentum and clamp
				_.#momentumEngine.stop();
				_.#activeEngine = null;
				_.syncIndicesFromPosition(minPos, 0);
				_.setPos(minPos, 1, 0, 'momentum', 0);
				_.#emitMovementCompleted('momentum');
				return;
			} else if (position < maxPos) {
				// Hit right boundary - stop momentum and clamp
				_.#momentumEngine.stop();
				_.#activeEngine = null;
				_.syncIndicesFromPosition(maxPos, 0);
				_.setPos(maxPos, 1, 0, 'momentum', 0);
				_.#emitMovementCompleted('momentum');
				return;
			}
		}

		if (options.snap === 'slide' && Math.abs(velocity) < SNAP_THRESHOLD) {
			// GEAR SHIFT: Momentum → Settle (adaptive friction)
			_.#momentumEngine.stop();

			// Project where we'd naturally coast to based on remaining velocity
			// Using friction decay: final position ≈ current + velocity * friction / (1 - friction)
			// Clamp friction to prevent division by zero when friction → 1
			const friction = options.animation?.freeScrollFriction || 0.96;
			const safeFriction = Math.min(friction, 0.999);
			const projectedPos = position + velocity * (safeFriction / (1 - safeFriction));

			// Find nearest slide to projected stopping point
			const slideAndGap = widths.slide + widths.gap;
			let absProjectedPos = Math.abs(projectedPos);
			if (options.centerSelectedSlide) {
				absProjectedPos = absProjectedPos + widths.viewport / 2 - widths.slide / 2;
			} else {
				absProjectedPos = absProjectedPos + widths.paddingLeft;
			}
			const projectedIndex = absProjectedPos / slideAndGap;

			// Bias toward "forward" direction - if more than 40% past a slide, go to next
			// This gives SettleEngine more distance to spread out the deceleration
			let targetIndex;
			if (velocity < 0) {
				// Moving forward (higher indices) - round up at 40%
				targetIndex = Math.floor(projectedIndex + 0.6);
			} else {
				// Moving backward (lower indices) - round down at 60%
				targetIndex = Math.floor(projectedIndex + 0.4);
			}

			// Handle bounds based on loop mode
			let positionIndex = targetIndex; // Index used for position calculation
			if (state.canLoop) {
				// For looping: use unwrapped index for position (allows animating beyond bounds)
				// Track shift will handle actual wrapping
				// Wrap targetIndex only for state updates
				targetIndex = ((targetIndex % slideCount) + slideCount) % slideCount;
			} else {
				// Non-looping: clamp to valid range
				targetIndex = Math.max(0, Math.min(targetIndex, slideCount - 1));
				positionIndex = targetIndex;
			}
			const slidesPerMove = options.slidesPerMove || 1;
			const targetPageIndex = Math.max(
				0,
				Math.min(Math.floor(targetIndex / slidesPerMove), state.pageCount - 1)
			);
			const targetPos = getTrackPosForIndex(positionIndex, widths, options, state);
			const distanceToTarget = Math.abs(targetPos - position);

			// Update state BEFORE emitting any events
			_.ctx.store.setState({
				renderIndex: targetIndex,
				pageIndex: targetPageIndex,
			});

			// If we're already very close to target, just snap there immediately
			if (distanceToTarget < 5) {
				_.setPos(targetPos, 1, 0, 'settle', 0);
				_.#activeEngine = null;
				_.#emitMovementCompleted('settle');
				return;
			}

			// Engage settle gear with adaptive friction for smooth iOS-like landing
			_.#targetPos = targetPos;
			_.#movementType = 'settle';
			_.#settleEngine.start(position, velocity, targetPos);
			_.#activeEngine = _.#settleEngine;
		} else if (options.snap === 'none' && Math.abs(velocity) < STOP_THRESHOLD) {
			// NEUTRAL: Just stop wherever we are
			_.syncIndicesFromPosition(position, velocity);
			_.#momentumEngine.stop();
			_.#activeEngine = null;
			_.setPos(position, 1, 0, 'momentum', 0);
			_.#emitMovementCompleted('momentum');
		} else {
			// Still in momentum — sync indices for live updates
			_.syncIndicesFromPosition(position, velocity);
		}
	}

	#emitMovementStarted(velocity, movementType) {
		const ctx = this.ctx;
		const state = ctx.store.getState();
		ctx.emitter.emit(ctx.events.movement.started, {
			renderIndex: state.renderIndex,
			pageIndex: state.pageIndex,
			velocity,
			movementType,
		});
	}

	#emitMovementCompleted(movementType) {
		const ctx = this.ctx;
		const state = ctx.store.getState();
		ctx.emitter.emit(ctx.events.movement.completed, {
			renderIndex: state.renderIndex,
			pageIndex: state.pageIndex,
			movementType,
		});
	}

	destroy() {
		const _ = this;

		// clean up physics engine and its events
		if (_.engine) {
			_.engine.off('engine:position-changed', _.handlers.enginePositionChanged);
			_.engine.off('engine:finished', _.handlers.engineMovementFinished);
			_.engine.stop();
			_.engine = null;
		}

		// clean up momentum engine
		if (_.#momentumEngine) {
			_.#momentumEngine.stop();
			_.#momentumEngine = null;
		}

		// clean up settle engine
		if (_.#settleEngine) {
			_.#settleEngine.off('engine:position-changed', _.handlers.settlePositionChanged);
			_.#settleEngine.off('engine:finished', _.handlers.settleFinished);
			_.#settleEngine.destroy();
			_.#settleEngine = null;
		}

		_.#activeEngine = null;

		// remove context event listeners using specific handler references
		const { emitter, events } = _.ctx;
		emitter.off(events.store.optionsChanged, _.handlers.optionsChanged);
		emitter.off(events.drag.start, _.handlers.dragStart);
		emitter.off(events.drag.move, _.handlers.dragMove);
		emitter.off(events.drag.end, _.handlers.dragEnd);
		emitter.off(events.track.requestFrame, _.handlers.requestTrackFrame);

		// clear handlers reference
		_.handlers = null;
	}
}
